import { SelectOption, useNotify } from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Typography } from '@material-ui/core'
// import { AssignmentInd } from '@material-ui/icons';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { AddressTypeEnum } from '../../api/Argentia/enums/addressTypeEnum'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import { BreadcrumbLink } from '../../components/shared/breadcrumbLink/breadcrumbLink'
import { enumEnumToValueString } from '../../helpers'
import { missingFieldsForm } from '../../helpers/validateFormHelper'
import { GlobalNotification, NonAuthRoutes } from '../../model'
import { fetchClientDataByID, notifyError, setDesactiveContract } from '../../redux'
import { selectActiveContract, selectClientData } from '../../redux/selectors/client.selectors'
import { fetchCountries, fetchMunicipalities, fetchProvinces } from '../clients/client.service'
import { FiscalAddressModel } from '../clients/clients.model'
import {
  AddressDataFormModel,
  addressTypeMap,
  EnumAddressNumberMap,
  initialIsDirty,
  IsDirtyType,
  sectionsAddressData
} from './addresses.model'
import { fetchAddresses, saveAddress } from './addresses.service'
import { AddressForm } from './components/addressForm'
import { getLinkClientText, isSubjectCommercialOrWholesaler } from '../clients/client.helper'
import { contractModelToContractFormMapper } from '../contracts/contracts.mapper'
import { useShowGoToProcedures } from '../../hooks/useShowGoToProceduresDialog'
import { hasResource } from '../../helpers/permissionsHelper'
import { Resources } from '../../model/RBAC/constants'
import { EnumCommodity } from '../../api/Argentia/enums'
import { ProcedureFormMode } from '../procedures/procedures.types'
import { MunicipalityModel, ProvinceModel } from '../../api/Argentia/model/addresses'

interface Addresses {
  type: AddressTypeEnum
  state: AddressDataFormModel | undefined
}

export const AddressInfoPage: React.FC<any> = props => {
  const history = useHistory()

  const [t] = useTranslation(['addresses', 'globalerisLibrary', 'enums'])
  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { getShowGoToProceduresDialog } = useShowGoToProcedures()
  const contractData = contractModelToContractFormMapper(useSelector(selectActiveContract))

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<IsDirtyType>(initialIsDirty)
  const { info } = useNotify()

  const contractCode: string = props.location.state.contractCode
  const contractID: number = props.location.state.contractID
  const documentID: string = props.location.state.documentID
  // const [clientID] = useState<number>(props.location.state.clientID)
  const clientData = useSelector(selectClientData)

  const [addresses, setAddresses] = useState<Addresses[]>([
    { type: AddressTypeEnum.Billing, state: undefined },
    { type: AddressTypeEnum.Supply, state: undefined }
  ])

  // Hooks Region Starts
  const onUpdateAddress = async () => {
    const addressesToUpdate = addresses.filter(address => address.state)

    const invalidForms = addressesToUpdate.some(address =>
      missingFieldsForm(sectionsAddressData, address.state)
    )

    if (invalidForms) {
      return notifyError(t('addresses:notifications.edit.errorInvalidForm'))
    }

    const saveRequests: Promise<any>[] = addressesToUpdate.map(async address => {
      return new Promise((resolve, reject) =>
        saveAddress(address.state!, address.type)
          .then(response => resolve(response))
          .catch(err => reject(err))
      )
    })

    return await Promise.all(saveRequests)
      .then(responses => {
        if (responses.every(response => response.success)) {
          info(t('addresses:notifications.edit.success'))
          return true
        } else {
          notifyError(t('addresses:notifications.edit.error'))
          return false
        }
      })
      .catch(err => {
        notifyError(t('addresses:notifications.edit.error'))
        return false
      })
  }

  const onAddressChange = (addressType: AddressTypeEnum, data: AddressDataFormModel) => {
    const updatedAddresses = addresses.map(address => {
      if (address.type === addressType) {
        return { ...address, state: data }
      }
      return address
    })

    setAddresses(updatedAddresses)
  }

  const onErrorsChange = (error: boolean, formName: string) => {
    if (formName) {
      setIsDirty({ ...isDirty, [formName]: error })
    }
  }

  const GoToClientFile = (clientID: number) => {
    history.push(NonAuthRoutes.ClientFile, {
      clientID: clientID
    })
  }

  const handleSave = async () => {
    setIsFetching(true)
    await onUpdateAddress()
      .then(response => {
        if (response) {
          if (clientData?.id) {
            fetchClientDataByID(clientData.id)
            // If user has Permission and Contract has been created with products, ask if they want to go to procedures add page
            if (
              contractData &&
              contractData.productsList &&
              contractData.productsList.length > 0 &&
              hasResource(Resources.ComunicationsCenterProcedures) &&
              !isSubjectCommercialOrWholesaler(clientData?.type)
            ) {
              getShowGoToProceduresDialog({
                contractID: contractID,
                origin:
                  contractData.commodity === EnumCommodity.Electricidad
                    ? ProcedureFormMode.ALTA_CONTRATO_POWER
                    : ProcedureFormMode.ALTA_CONTRATO_GAS,
                cancelAction: () => GoToClientFile(clientData.id!)
              })
            } else {
              GoToClientFile(clientData.id)
            }
          } else {
            history.push(NonAuthRoutes.Client)
          }
        }
      })
      .finally(() => setIsFetching(false))
  }

  useEffect(() => {
    setIsFetching(true)

    const getAddresses = async () => {
      EnumAddressNumberMap.forEach(
        async type =>
          await fetchAddresses(contractID, +type).then(response => {
            if (response.success && response.data.length > 0) {
              setAddresses(prevState => {
                const updatedList = prevState.map(address => {
                  if (address.type === +type) {
                    // Se considera que hay 1 única dirección por tipo para un mismo contrato
                    const newAddress: FiscalAddressModel = response.data[0]
                    const mappedNewAddress: AddressDataFormModel = {
                      ...response.data[0],
                      municipality: newAddress.municipality,
                      province: newAddress.province,
                      country: newAddress.country
                    }
                    return { ...address, state: mappedNewAddress }
                  }
                  return address
                })
                return updatedList
              })
              //TODO: de momento solo va a devolver una dirección porque todavía
              //el contrato no es multicups
            }
          })
      )
    }

    getAddresses().finally(() => {
      setIsFetching(false)
    })

    return () => {
      setDesactiveContract()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hooks Region Ends
  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('addresses:titles.principal')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={isFetching}
          onClick={() => {
            history.push(NonAuthRoutes.Client)
          }}
        >
          {t('addresses:buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isFetching || isDirty?.Billing || isDirty?.Supply}
          onClick={handleSave}
        >
          {t('addresses:buttons.saveAndLeave')}
        </Button>
      </PageHeader>
      <Box mb={1} ml={2}>
        <Breadcrumbs separator=">" aria-label="breadcrumb">
          {clientData && (
            <BreadcrumbLink
              toPathName="/client/edit/"
              state={{ subjectType: undefined, clientID: clientData?.id }}
            >{`Cliente ${getLinkClientText(clientData)} - ID ${
              clientData.documentID
            }`}</BreadcrumbLink>
          )}
          {(contractID || contractID) && (
            <BreadcrumbLink
              // toPathName="/contract/edit/"
              toPathName="/client/edit" // temporal
              // state={{ subjectType: undefined, clientID: clientID }}
            >
              Contrato {contractData?.contractCode}
            </BreadcrumbLink>
          )}
          <Typography key="3" color="primary">
            {t('addresses:titles.principal')}
          </Typography>
        </Breadcrumbs>
      </Box>
      {addresses
        .filter(address => address.state)
        .map(address => {
          return (
            <AddressForm
              key={`${address.type}-${enumEnumToValueString(AddressTypeEnum, address.type)}`}
              address={address.state}
              addressType={address.type}
              onDataChange={data => onAddressChange(address.type, data)}
              onError={error =>
                onErrorsChange(error, enumEnumToValueString(AddressTypeEnum, address.type) ?? '')
              }
            />
          )
        })}
    </Page>
  )
}
